// Media Lightbox component with next/prev navigation (WP-04).
import { useEffect, useId } from 'react';
import { useI18n } from '../../lib/i18n';
import type { InboxMessage } from '../../lib/inboxTypes';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from '../icons';

interface MediaLightboxProps {
  messages: InboxMessage[];
  currentMessageId: string;
  onClose: () => void;
  onNavigate: (messageId: string) => void;
}

export default function MediaLightbox({
  messages,
  currentMessageId,
  onClose,
  onNavigate,
}: MediaLightboxProps) {
  const { t } = useI18n();
  const labelId = useId();
  const mediaList = messages.filter((m) => m.mediaUrl);
  const currentIndex = mediaList.findIndex((m) => m.id === currentMessageId);
  const currentMedia = mediaList[currentIndex] || null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(mediaList[currentIndex - 1].id);
      } else if (e.key === 'ArrowRight' && currentIndex < mediaList.length - 1) {
        onNavigate(mediaList[currentIndex + 1].id);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, mediaList, onClose, onNavigate]);

  if (!currentMedia || !currentMedia.mediaUrl) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < mediaList.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-md"
    >
      {/* Top Bar */}
      <div className="absolute start-0 end-0 top-0 flex items-center justify-between p-4 z-10 text-white">
        <div id={labelId} className="text-xs font-semibold text-slate-300">
          {currentIndex + 1} / {mediaList.length} · {currentMedia.time}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={currentMedia.mediaUrl}
            target="_blank"
            rel="noreferrer"
            download
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            title={t('common.download') || 'Download'}
          >
            <DownloadIcon size={16} />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label={t('common.close') || 'Close'}
          >
            <CloseIcon size={18} />
          </button>
        </div>
      </div>

      {/* Prev Button */}
      {hasPrev && (
        <button
          type="button"
          onClick={() => onNavigate(mediaList[currentIndex - 1].id)}
          className="absolute start-4 top-1/2 -translate-y-1/2 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          aria-label={t('common.previous')}
        >
          <ChevronLeftIcon size={22} className="rtl:rotate-180" />
        </button>
      )}

      {/* Media Content */}
      <div className="max-h-[85vh] max-w-[85vw] flex flex-col items-center justify-center">
        {currentMedia.mediaType === 'video' ? (
          <video
            src={currentMedia.mediaUrl}
            controls
            autoPlay
            className="max-h-[80vh] max-w-[85vw] rounded-lg shadow-2xl"
          />
        ) : (
          <img
            src={currentMedia.mediaUrl}
            alt={currentMedia.body || ''}
            className="max-h-[80vh] max-w-[85vw] object-contain rounded-lg shadow-2xl select-none"
          />
        )}
        {currentMedia.body && currentMedia.body !== '[media]' && (
          <p className="mt-3 max-w-xl text-center text-xs text-white/90 bg-black/60 px-4 py-2 rounded-full backdrop-blur-sm">
            {currentMedia.body}
          </p>
        )}
      </div>

      {/* Next Button */}
      {hasNext && (
        <button
          type="button"
          onClick={() => onNavigate(mediaList[currentIndex + 1].id)}
          className="absolute end-4 top-1/2 -translate-y-1/2 z-10 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
          aria-label={t('common.next')}
        >
          <ChevronRightIcon size={22} className="rtl:rotate-180" />
        </button>
      )}
    </div>
  );
}
